import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../component/AuthContext";
import Dialog from "../component/ui/Dialog";
import "./sessionWarning.css";

// WCAG 2.2.1 Timing Adjustable: a signed-in session used to end with no
// warning. Two minutes before the sign-in token expires this asks the member
// whether to stay signed in, and "Stay signed in" swaps the token for a fresh
// one through the same /auth/refresh-token call the app uses.

const WARN_SECONDS = 120;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const secondsLeft = () => {
  const token = sessionStorage.getItem("jwtToken");
  if (!token) return null;
  try {
    const { exp } = JSON.parse(atob(token.split(".")[1]));
    return exp ? Math.floor(exp - Date.now() / 1000) : null;
  } catch (e) {
    return null;
  }
};

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const SessionWarning = () => {
  const { isLoggedIn, logout } = useAuth();
  const [left, setLeft] = useState(null); // seconds, while the warning shows
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const open = left !== null;

  const stay = useCallback(async () => {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) {
      setFailed(true);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      const json = await res.json();
      const data = json?.data || json;
      if (!res.ok || !data?.token) throw new Error("refresh failed");
      sessionStorage.setItem("jwtToken", data.token);
      if (data.refreshToken) sessionStorage.setItem("refreshToken", data.refreshToken);
      setFailed(false);
      setLeft(null);
    } catch (e) {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, []);

  // Escape (and the close button) keep the member signed in: closing the warning should never be
  // what signs them out.

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const tick = () => {
      const s = secondsLeft();
      if (s === null) return;
      if (s <= 0) {
        setLeft(null);
        logout(true);
      } else if (s <= WARN_SECONDS) {
        setLeft(s);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isLoggedIn, logout]);

  if (!open) return null;

  // The close button (and Escape) mean "stay signed in": gone once that has failed, and a no-op while a
  // refresh is already on its way (a second click must not send a second refresh call).
  const noop = () => {};
  const onClose = failed ? undefined : busy ? noop : stay;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      role="alertdialog"
      eyebrow="Your session"
      title="Still there?"
      titleId="sess-title"
      describedBy="sess-text"
      closeLabel="Stay signed in"
    >
      <p className="sess-text" id="sess-text">
        To keep your account safe you will be signed out in{" "}
        {/* The ticking clock is for sighted readers; the spoken text is read once. */}
        <span className="sess-clock" aria-hidden="true">
          {clock(left)}
        </span>
        <span className="sr-only">
          about {Math.ceil(left / 60)} {Math.ceil(left / 60) === 1 ? "minute" : "minutes"}
        </span>
        . Anything you have not saved will be lost.
      </p>
      {failed && (
        <p className="fn-error" role="alert">
          We could not keep you signed in. Please sign in again.
        </p>
      )}
      <div className="sess-actions">
        <button type="button" className="fn-btn fn-btn-secondary" onClick={() => logout(false)}>
          Sign out now
        </button>
        {!failed && (
          <button type="button" className="fn-btn fn-btn-primary" onClick={stay} disabled={busy}>
            {busy ? "Please wait\u2026" : "Stay signed in"}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default SessionWarning;
